import { useAppDispatch } from '@core/redux/hooks';
import {
  decreaseLoading,
  increaseLoading,
} from '@features/layout/store/layout.slice';
import { coupons } from '@service/api';
import {
  createDigitalCoupon,
  TCreateDigitalCouponProps,
} from '@service/api/coupons/digital/createDigitalCoupon';
import {
  editDigitalCoupon,
  TEditDigitalCouponProps,
} from '@service/api/coupons/digital/editDigitalCoupon';
import { useQuery } from '@tanstack/react-query';
import { PageLimitFilter } from '@typings';
import { Modal } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isSerializedApiError } from 'src/app/utils/isSerializedApiError';
import { useBreadcrumbs } from 'src/features/layout/hooks/useBreadcrumbs';
import { offerApi } from 'src/features/offers/apis/offerApis';
import { CouponDigital } from 'src/models';

import OpenNotification from '../../../../components/antd/OpenNotification';
import { discountMock } from '../digital/mocks/digitalCoupon.mock';
import { TableCouponDigitalProps } from '../digital/TableDigital.coupons';
import { TFormData } from '../digital/types/formDataType';

export const useProductDigitalCoupon = () => {
  const dispatch = useAppDispatch();

  const [couponList, setCouponList] = useState<CouponDigital.TCouponResponse>();

  const [currentFilter, setCurrentFilter] = useState<PageLimitFilter>();
  const [isLoadingData, setIsLoadingData] = useState(true);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  useBreadcrumbs([
    {
      label: 'Gestão de Cupons',
    },
    {
      label: 'Produto Digital',
    },
  ]);

  const getAllCoupons = useCallback(async (props?: PageLimitFilter) => {
    try {
      setIsLoadingData(true);
      dispatch(increaseLoading());
      if (props) setCurrentFilter(props);

      if (!props?.limit || !props.page) {
        props = {
          ...props,
          limit: 20,
          page: 1,
        };
      }
      const response = await coupons.getDigitalCoupons(props);
      setCouponList(response);
    } catch (err) {
      console.error(err);
      OpenNotification(true, 'Falha ao carregar os cupons do sistema.');
    } finally {
      dispatch(decreaseLoading());
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    getAllCoupons();
  }, []);

  const handleChangePagination: TableCouponDigitalProps['onChangePagination'] =
    (take, _, pagination) => {
      getAllCoupons({
        ...currentFilter,
        limit: take,
        page: pagination,
      });
    };

  const handleFilterCoupon = useCallback(
    (filterList: { field: string; value?: string }[]) => {
      const current = {
        ...currentFilter,
        filter: {
          ...currentFilter?.filter,
        },
      };

      filterList.forEach(filterToAdd => {
        if (!filterToAdd.value) {
          delete current.filter?.[filterToAdd.field];
        } else {
          current.filter[filterToAdd.field] = filterToAdd.value;
        }
      });

      getAllCoupons(current as PageLimitFilter);
    },
    [getAllCoupons, currentFilter]
  );

  const handleOrderCoupon = useCallback(
    (order: string) => {
      getAllCoupons({ ...currentFilter, order });
    },
    [getAllCoupons, currentFilter]
  );

  const dispatchSubmit = useCallback(() => {
    formRef.current?.submit();
  }, [formRef.current]);

  const onSaveCoupon = useCallback(() => {
    Modal.success({
      title: 'Concluído!',
      content: 'Cupom salvo com sucesso!',
    });
    navigate('/screens/coupons?show=digital');
  }, [navigate]);

  const handleCancel = useCallback(() => {
    navigate('/screens/coupons?show=digital');
  }, [navigate]);

  const INFINITE_DISCOUNT_LIMIT = 10000000;

  const { isLoading: isLoadingProductOptions, data: productOptionsResult } =
    useQuery({
      queryKey: ['product-options'],
      queryFn: () => offerApi.getProducts(),
      select: (products: any) => {
        return products.map((product: any) => {
          return {
            label: product.name,
            value: Number(product.id),
          };
        });
      },
      refetchOnWindowFocus: false,
    });

  const productOptions = useMemo(() => {
    return productOptionsResult || [];
  }, [productOptionsResult]);

  const getInvalidOfferIds = useCallback(async (offerIds: number[]) => {
    const validationPromises = offerIds.map(id =>
      offerApi.getOfferById(String(id))
    );
    const results = await Promise.allSettled(validationPromises);

    const allValid = results.every((result: any) => result.value !== undefined);
    if (allValid) return null;

    return results
      .map((result, index) => ((result as any).value ? null : offerIds[index]))
      .filter(id => id !== null);
  }, []);

  const submitCreateCoupon = async (formData: TFormData) => {
    const {
      code,
      description,
      startDate,
      endDate,
      limitValue,
      onlyFirstCycle,
      purchaseTypeAllowed,
      allowedProducts,
      allowedOffers,
      typeDiscount,
      value,
      active,
    } = formData;

    const payload: TCreateDigitalCouponProps = {
      code,
      description,
      onlyFirstCycle,
      purchaseTypeAllowed,
      active,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      allowedProducts: allowedProducts || [],
      allowedOffers: allowedOffers || [],
      discount: {
        type: typeDiscount,
        value: Number(value || '0'),
      },
      maxUse: Number(limitValue) || INFINITE_DISCOUNT_LIMIT,
      maxUsePerProduct: Number(limitValue) || INFINITE_DISCOUNT_LIMIT,
      maxUsePerCustomer: 1,
    };
    await createDigitalCoupon(payload);
  };

  const submitEditCoupon = async (couponId: number, formData: TFormData) => {
    const {
      description,
      startDate,
      endDate,
      limitValue,
      purchaseTypeAllowed,
      allowedProducts,
      allowedOffers,
      typeDiscount,
      value,
      active,
    } = formData;

    const payload: TEditDigitalCouponProps = {
      id: couponId,
      description,
      purchaseTypeAllowed,
      active,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      allowedProducts: allowedProducts || [],
      allowedOffers: allowedOffers || [],
      discount: {
        type: typeDiscount,
        value: Number(value || '0'),
      },
      maxUse: Number(limitValue) || INFINITE_DISCOUNT_LIMIT,
      maxUsePerProduct: Number(limitValue) || INFINITE_DISCOUNT_LIMIT,
      maxUsePerCustomer: 1,
    };
    await editDigitalCoupon(payload);
  };

  const handleDisplayError = (err: unknown) => {
    console.error(err);
    if (isSerializedApiError(err))
      return err.message || `Problema de comunicação com a API`;
    return `Erro ao salvar. Contate a equipe técnica.`;
  };

  return {
    couponList,
    isLoadingData,
    handleChangePagination,
    handleFilterCoupon,
    handleOrderCoupon,
    dispatchSubmit,
    onSaveCoupon,
    handleCancel,
    formRef,
    isLoading,
    setIsLoading,
    INFINITE_DISCOUNT_LIMIT,
    productOptions,
    isLoadingProductOptions,
    submitCreateCoupon,
    submitEditCoupon,
    handleDisplayError,
    getInvalidOfferIds,
  };
};
